import React from 'react'
import styled from 'styled-components'
import { Column } from 'components/common/layout'
import { BaseText, SemiBoldText } from 'components/common/text'
import { TagContainer, Tags } from 'components/common/tag'
import { WithIdName } from 'types/common'
import { CHECK_HTML } from 'infra/constants'
import { TextViewer } from 'components/common/text-editor'
import { Hashtag } from 'components/common/hashtag'
import { Map } from 'components/startup/startup-map'

const convertLi = (li: string) => {
  const array = li?.split('\n').filter((item) => item !== '')
  const result = []
  for (let i = 0; i < array.length; i++) {
    const item = array[i]
    if (item.startsWith('-')) {
      const lastEle = result.at(-1)
      const subLi = item.slice(1).trim()
      if (Array.isArray(lastEle)) {
        lastEle.push(subLi)
      } else {
        result.push([subLi])
      }
    } else result.push(item)
  }
  return result
}

const ListItem: React.FC<{ content: string }> = ({ content }) => (
  <li>
    <ListText>{content}</ListText>
  </li>
)

const List: React.FC<{ data: string }> = ({ data }) => {
  return CHECK_HTML.test(data) ? (
    <TextViewer value={data} />
  ) : (
    <UL>
      {convertLi(data)?.map((item, index) =>
        typeof item === 'string' ? (
          <ListItem key={index} content={item} />
        ) : (
          <UL key={index}>
            {item.map((li, index) => (
              <ListItem key={index} content={li} />
            ))}
          </UL>
        ),
      )}
    </UL>
  )
}

export const PositionDetail: React.FC<{
  data: {
    task: string
    qualification: string
    preferred: string
    hiringProcess: string
    expectationKeywords: WithIdName[]
    techStacks: string[]
    location: string
    address: string
  }
  width?: number
}> = ({ data, width = 600 }) => {
  const {
    task,
    qualification,
    preferred,
    hiringProcess,
    expectationKeywords,
    techStacks,
    location,
    address,
  } = data
  return (
    <Column style={{ gap: 64, width: '100%' }}>
      {techStacks.length > 0 && (
        <InfoWrapper>
          <InfoTitle>스킬</InfoTitle>
          <TagContainer>
            <Tags data={techStacks} />
          </TagContainer>
        </InfoWrapper>
      )}
      {task && (
        <InfoWrapper>
          <InfoTitle>주요업무</InfoTitle>
          <ListCard>
            <List data={task} />
          </ListCard>
        </InfoWrapper>
      )}
      {qualification && (
        <InfoWrapper>
          <InfoTitle>자격요건</InfoTitle>
          <ListCard>
            <List data={qualification} />
          </ListCard>
        </InfoWrapper>
      )}
      {preferred && (
        <InfoWrapper>
          <InfoTitle>우대사항</InfoTitle>
          <ListCard>
            <List data={preferred} />
          </ListCard>
        </InfoWrapper>
      )}
      {hiringProcess && (
        <InfoWrapper>
          <InfoTitle>채용절차</InfoTitle>
          <ListCard>
            <List data={hiringProcess} />
          </ListCard>
        </InfoWrapper>
      )}
      {expectationKeywords && (
        <InfoWrapper>
          <InfoTitle>합류하면 얻을 수 있는 것</InfoTitle>
          <Hashtag
            data={expectationKeywords.map((x) => x.name.replaceAll(' ', '_'))}
          />
        </InfoWrapper>
      )}
      <InfoWrapper>
        <InfoTitle>근무지</InfoTitle>
        <MapContainer>
          <Map
            mapId='position-map'
            width={width}
            height={width * (9 / 16)}
            address={address}
          />
          <AddressText>
            {address} ({location.split(' ').join(', ')})
          </AddressText>
        </MapContainer>
      </InfoWrapper>
    </Column>
  )
}

const AddressText = styled(BaseText)`
  padding: 20px;
  @media ${(p) => p.theme.deviceSize.mobile} {
    padding: 16px;
    font-size: ${(p) => p.theme.fontSize.small};
  }
`

const MapContainer = styled(Column)`
  overflow: hidden;
  border: 1px solid ${(p) => p.theme.color.line};
  border-radius: 12px;
`

const InfoWrapper = styled(Column)`
  gap: 24px;
`
const InfoTitle = styled(SemiBoldText)`
  font-size: ${(p) => p.theme.fontSize.h4};
  @media ${(p) => p.theme.deviceSize.mobile} {
    font-size: ${(p) => p.theme.fontSize.h5};
  }
`

const ListText = styled(BaseText)`
  line-height: 1.8;
`

const UL = styled.ul`
  padding-left: 16px;
`

const ListCard = styled(Column)`
  padding: 24px;
  border-radius: 12px;
  background-color: ${(p) => p.theme.color.background};
  @media ${(p) => p.theme.deviceSize.mobile} {
    padding: 16px;
  }
`
